/**
 * SurveyComplete — Finalize survey, review summary, trigger sync + report review.
 * Route: /visits/:visitId/survey/complete
 */

import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, CheckCircle, AlertCircle, AlertTriangle, Loader2, Upload, FileText, ClipboardList } from 'lucide-react'
import { completeSurvey, getSurvey, getWalkthroughState, type SurveySession, type WalkthroughState } from '../../lib/services/survey'
import { surveySyncEngine } from '../../lib/services/surveySync'
import { surveyStore } from '../../lib/offline/surveyStore'
import { blobToDataUrl } from '../../lib/offline/photoPreview'
import { detectPatterns, detectConditionLevel, type PatternAlert, type ConditionAlert, type AnalyzableFinding } from '../../lib/findingAnalysis'
import { invokeFunction } from '../../lib/api'
import { isAuthFailure, authFailureMessage, shouldRetryRequest } from '../../lib/errors'
import { safeLogger } from '../../lib/logger'
import { SyncStatus } from '../../components/survey/SyncStatus'
import { FieldAppShell } from '../../components/survey/FieldAppShell'
import { calculateScoreAndGenerateDraft } from '../../lib/services/reports'
import { useAuth } from '../../context/AuthContext'
import {
  computeWalkthroughAssessmentCoverage,
  type AssessmentCoverage,
} from '../../lib/walkthroughAssessmentCoverage'

function canTriggerEvaluatorExport(viewerContext: ReturnType<typeof useAuth>['viewerContext']): boolean {
  return viewerContext?.role === 'Admin' || viewerContext?.is_superadmin === true || viewerContext?.is_backend_superadmin === true
}

function evaluatorAccreditingBody(value: string | null | undefined): 'CMS' | 'TJC' | 'AAAHC' | 'STATE' {
  const normalized = value?.trim().toUpperCase()
  if (normalized === 'TJC' || normalized === 'AAAHC' || normalized === 'STATE') return normalized
  return 'CMS'
}

async function cacheSurveyEvidencePreviews(surveyId: string): Promise<void> {
  const photos = await surveyStore.getPhotosBySurvey(surveyId)
  await Promise.all(photos.map(async (photo) => {
    if (!(photo.blob instanceof Blob)) return
    const dataUrl = await blobToDataUrl(photo.blob)
    await surveyStore.saveEvidencePreview({
      visit_id: photo.visit_id,
      evidence_id: photo.photo_id,
      data_url: dataUrl,
    })
  }))
}

export function SurveyComplete() {
  const { visitId } = useParams<{ visitId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { viewerContext } = useAuth()

  const { data: remoteSession = null, isLoading: isSurveyLoading } = useQuery({
    queryKey: ['survey', visitId],
    queryFn: () => getSurvey(visitId!),
    enabled: !!visitId,
    retry: shouldRetryRequest,
  })

  const [offlineSession, setOfflineSession] = useState<SurveySession | null>(null)
  const [pendingCount, setPendingCount] = useState(0)
  const [counts, setCounts] = useState({ findings: 0, photos: 0, documents: 0, transcripts: 0 })
  const [syncResult, setSyncResult] = useState<{ processed: number; failed: number } | null>(null)
  const [isSyncing, setIsSyncing] = useState(false)
  const [patterns, setPatterns] = useState<PatternAlert[]>([])
  const [conditionAlerts, setConditionAlerts] = useState<ConditionAlert[]>([])
  const [isDraftGenerating, setIsDraftGenerating] = useState(false)
  const [draftError, setDraftError] = useState<string | null>(null)
  const [walkthroughState, setWalkthroughState] = useState<WalkthroughState | null>(null)
  const [partialConfirmOpen, setPartialConfirmOpen] = useState(false)
  const [partialAcknowledged, setPartialAcknowledged] = useState(false)

  useEffect(() => {
    if (!visitId) return
    surveyStore.getSessionByVisit(visitId).then((s) => setOfflineSession(s ?? null))
  }, [visitId])

  const session = remoteSession ?? offlineSession

  useEffect(() => {
    if (!session || !visitId) return
    let cancelled = false
    Promise.all([
      surveyStore.getFindingsBySurvey(session.survey_id),
      surveyStore.getPhotosBySurvey(session.survey_id),
      surveyStore.getDocumentsBySurvey(session.survey_id),
      surveyStore.getTranscriptsBySurvey(session.survey_id),
      surveyStore.getQueuedBySurvey(session.survey_id),
      surveyStore.getWalkthroughState(session.survey_id).catch(() => null),
      getWalkthroughState(visitId, session.survey_id).catch(() => null),
    ]).then(([f, p, d, t, q, localWt, remoteWt]) => {
      if (cancelled) return
      setCounts({ findings: f.length, photos: p.length, documents: d.length, transcripts: t.length })
      setPendingCount(q.length)
      setWalkthroughState((remoteWt as WalkthroughState | null) ?? (localWt as WalkthroughState | null) ?? null)

      // Run regulatory analysis
      const analyzable: AnalyzableFinding[] = f.map((finding) => ({
        finding_id: finding.finding_id,
        domain: finding.domain,
        standard_ref: finding.standard_ref,
        severity: finding.severity,
        observation: finding.observation,
        area: finding.area,
      }))
      setPatterns(detectPatterns(analyzable))
      setConditionAlerts(detectConditionLevel(analyzable))
    })
    return () => { cancelled = true }
  }, [session, visitId])

  const assessmentCoverage: AssessmentCoverage = computeWalkthroughAssessmentCoverage(
    walkthroughState?.stepStates ?? null,
  )
  /** Partial confirm only when walkthrough state is known (guided path). */
  const needsPartialConfirm = Boolean(walkthroughState) && assessmentCoverage.isPartial

  const handleForceSync = useCallback(async () => {
    setIsSyncing(true)
    const result = await surveySyncEngine.sync()
    setSyncResult(result)
    // Refresh counts
    if (session) {
      const q = await surveyStore.getQueuedBySurvey(session.survey_id)
      setPendingCount(q.length)
    }
    setIsSyncing(false)
  }, [session])

  const handleGenerateDraft = useCallback(async () => {
    if (!visitId) return
    setIsDraftGenerating(true)
    setDraftError(null)
    try {
      // #1799: calculate score first so reports:draft has a scores row
      await calculateScoreAndGenerateDraft(visitId)
      navigate(`/visits/${visitId}/survey/report`)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to generate report.'
      setDraftError(message)
      safeLogger.logError('ai_draft_report_failed', crypto.randomUUID())
    } finally {
      setIsDraftGenerating(false)
    }
  }, [visitId, navigate])

  const completeMutation = useMutation({
    mutationFn: async () => {
      if (!visitId || !session) throw new Error('No active survey')

      // 1. Final sync
      const syncResult = await surveySyncEngine.sync()
      const [pendingCountAfterSync, failedCount, terminalFailedCount] = await Promise.all([
        surveySyncEngine.pendingCount(),
        surveySyncEngine.failedCount(),
        surveySyncEngine.terminalFailedCount(),
      ])
      setSyncResult(syncResult)
      setPendingCount(pendingCountAfterSync)

      if (syncResult.failed > 0 || pendingCountAfterSync > 0 || failedCount > 0 || terminalFailedCount > 0) {
        throw new Error('Survey evidence is not fully synced yet. Resolve sync failures before completing the survey.')
      }

      // 2. Complete survey session
      const completed = await completeSurvey(visitId, session.survey_id)

      // 3. Optionally trigger evaluator-export edge function.
      // Field surveyors can complete surveys and edit the field report, but the
      // evaluator export endpoint is admin-scoped.
      if (canTriggerEvaluatorExport(viewerContext)) {
        try {
          const exportResult = await invokeFunction('evaluator-export', {
            method: 'POST',
            body: {
              site_visit_id: visitId,
              export_type: 'full_cycle',
              accrediting_body: evaluatorAccreditingBody(completed.framework || session.framework),
            },
          })
          if (exportResult.error) {
            safeLogger.logError('export_trigger_failed', exportResult.error.trace_id || crypto.randomUUID())
          }
        } catch {
          // Non-fatal — export can be re-triggered later
          safeLogger.logError('export_trigger_failed', crypto.randomUUID())
        }
      }

      // 4. Preserve device-local preview cache for report appendix recall, then
      // clean up the heavier offline survey payload.
      try {
        await cacheSurveyEvidencePreviews(session.survey_id)
      } catch {
        safeLogger.logError('evidence_preview_cache_failed', crypto.randomUUID())
      }

      // 5. Clean up local offline data
      await surveyStore.wipeSurvey(session.survey_id)

      return completed
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['survey', visitId] })
      await queryClient.invalidateQueries({ queryKey: ['visits'] })
      navigate(`/visits/${visitId}/survey/report`)
    },
  })

  if (isSurveyLoading && !session) {
    return (
      <FieldAppShell eyebrow="Survey" title="Complete Survey" backTo=".." backLabel="Back">
        <div className="flex flex-col items-center justify-center gap-3 py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--brand-primary)]" />
          <p className="text-sm font-medium text-slate-600">Loading survey summary...</p>
        </div>
      </FieldAppShell>
    )
  }

  if (!visitId || !session) {
    return (
      <FieldAppShell eyebrow="Survey" title="Complete Survey" backTo=".." backLabel="Back">
        <div className="px-4 py-6">
          <p className="text-sm text-slate-500">No active survey.</p>
          <Link to=".." className="btn-ghost text-sm mt-4 inline-block">Back</Link>
        </div>
      </FieldAppShell>
    )
  }

  const hasPending = pendingCount > 0
  const isCompleted = session.status === 'completed'

  return (
    <FieldAppShell eyebrow="Survey" title="Complete Survey" backTo=".." backLabel="Back" rightSlot={<SyncStatus />}>
    <div className="space-y-4 px-4 py-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Link to=".." className="text-slate-400 hover:text-slate-600">
            <ArrowLeft size={20} />
          </Link>
          <h2 className="section-title">Complete Survey</h2>
        </div>
        <SyncStatus />
      </div>

      {/* Summary */}
      <div className="app-surface p-4 space-y-3">
        <span className="section-title text-base">Summary</span>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="bg-slate-50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-[var(--brand-primary)]">{counts.findings}</p>
            <p className="text-xs text-slate-500">Findings</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-[var(--brand-primary)]">{counts.photos}</p>
            <p className="text-xs text-slate-500">Photos</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-[var(--brand-primary)]">{counts.documents}</p>
            <p className="text-xs text-slate-500">Documents</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-[var(--brand-primary)]">{counts.transcripts}</p>
            <p className="text-xs text-slate-500">Transcripts</p>
          </div>
        </div>
      </div>

      {/* Condition-level alerts */}
      {conditionAlerts.length > 0 && (
        <div className="space-y-2">
          {conditionAlerts.map((alert) => (
            <div
              key={alert.cfcSection}
              className={`app-surface p-4 space-y-2 border-l-4 ${
                alert.severity === 'critical' ? 'border-red-500 bg-red-50' : 'border-amber-400 bg-amber-50'
              }`}
            >
              <div className="flex items-center gap-2">
                <AlertTriangle size={16} className={alert.severity === 'critical' ? 'text-red-600' : 'text-amber-600'} />
                <span className={`text-sm font-bold ${alert.severity === 'critical' ? 'text-red-700' : 'text-amber-700'}`}>
                  {alert.severity === 'critical' ? 'Condition-Level Alert' : 'Pattern Warning'}: {alert.cfcTitle}
                </span>
              </div>
              <p className="text-xs text-slate-700 leading-relaxed">{alert.description}</p>
              <p className="text-[10px] text-slate-500">
                {alert.cfcSection} — {alert.findingCount} findings across {alert.standardRefs.join(', ')}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Pattern analysis */}
      {patterns.length > 0 && (
        <div className="app-surface p-4 space-y-3">
          <span className="section-title text-base">Pattern Analysis</span>
          <div className="space-y-2">
            {patterns.map((p) => (
              <div key={p.standardRef} className="flex items-start gap-2 py-1.5 border-b border-slate-100 last:border-0">
                <AlertTriangle size={14} className={p.suggestUpgrade ? 'text-amber-500 mt-0.5' : 'text-slate-400 mt-0.5'} />
                <div className="flex-1">
                  <p className="text-xs text-slate-700">
                    <span className="font-bold">{p.standardRef}</span> — {p.count} findings
                    {p.areas.length > 1 && ` across ${p.areas.length} areas`}
                  </p>
                  {p.suggestUpgrade && (
                    <p className="text-[10px] text-amber-600 font-semibold mt-0.5">
                      Consider upgrading isolated findings to Moderate (pattern)
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sync status */}
      {hasPending && (
        <div className="app-surface p-4 space-y-3">
          <div className="flex items-center gap-2 text-amber-600">
            <AlertCircle size={16} />
            <span className="text-sm font-semibold">{pendingCount} items pending sync</span>
          </div>
          <button
            type="button"
            onClick={handleForceSync}
            disabled={isSyncing}
            className="btn-ghost w-full text-sm"
          >
            {isSyncing ? (
              <><Loader2 size={14} className="inline mr-1 animate-spin" /> Syncing…</>
            ) : (
              <><Upload size={14} className="inline mr-1" /> Sync Now</>
            )}
          </button>
          {syncResult && (
            <p className="text-xs text-slate-500">
              Synced {syncResult.processed}, failed {syncResult.failed}
            </p>
          )}
        </div>
      )}

      {/* Assessment coverage / partial scope (one engine — partial always allowed) */}
      {!isCompleted && walkthroughState && (
        <div className="app-surface p-4 space-y-2" data-testid="assessment-coverage-panel">
          <span className="section-title text-base">Assessment coverage</span>
          <p className="text-xs text-slate-600" data-testid="assessment-coverage-summary">
            {assessmentCoverage.scopeSummary}
          </p>
          <div className="grid grid-cols-2 gap-2 text-center text-xs">
            <div className="bg-emerald-50 rounded-lg p-2">
              <p className="text-lg font-bold text-emerald-700">{assessmentCoverage.assessedItems}</p>
              <p className="text-slate-500">Assessed (scored)</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-2">
              <p className="text-lg font-bold text-slate-700">{assessmentCoverage.notAssessedItems}</p>
              <p className="text-slate-500">Not assessed (excluded)</p>
            </div>
          </div>
          {assessmentCoverage.isPartial && (
            <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2" data-testid="partial-scope-disclaimer">
              {assessmentCoverage.notAssessedDisclaimer}
            </p>
          )}
        </div>
      )}

      {/* Complete button — same path for full or partial submit */}
      {!isCompleted && (
        <button
          type="button"
          data-testid="complete-survey-btn"
          onClick={() => {
            if (needsPartialConfirm && !partialAcknowledged) {
              setPartialConfirmOpen(true)
              return
            }
            completeMutation.mutate()
          }}
          disabled={completeMutation.isPending || hasPending}
          className="btn-primary w-full py-3 text-sm disabled:opacity-60"
        >
          {completeMutation.isPending ? (
            <><Loader2 size={16} className="inline mr-2 animate-spin" /> Finalizing…</>
          ) : (
            <><CheckCircle size={16} className="inline mr-2" /> Complete Survey & Trigger Report Review</>
          )}
        </button>
      )}

      {partialConfirmOpen && !isCompleted && (
        <div
          className="app-surface p-4 space-y-3 border-2 border-amber-300 bg-amber-50"
          data-testid="partial-submit-confirm"
          role="dialog"
          aria-labelledby="partial-submit-title"
        >
          <p id="partial-submit-title" className="text-sm font-bold text-amber-900">
            Partial assessment — confirm submit
          </p>
          <p className="text-xs text-amber-900 leading-relaxed">
            {assessmentCoverage.confirmPrompt}
          </p>
          <p className="text-[11px] text-slate-700">
            Unassessed items are removed from the scoring methodology and are not included in report findings.
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              data-testid="partial-submit-cancel"
              className="btn-ghost flex-1 text-sm py-2"
              onClick={() => setPartialConfirmOpen(false)}
            >
              Keep assessing
            </button>
            <button
              type="button"
              data-testid="partial-submit-confirm-btn"
              className="btn-primary flex-1 text-sm py-2"
              disabled={completeMutation.isPending || hasPending}
              onClick={() => {
                setPartialAcknowledged(true)
                setPartialConfirmOpen(false)
                completeMutation.mutate()
              }}
            >
              Proceed with partial submit
            </button>
          </div>
        </div>
      )}

      {hasPending && !isCompleted && (
        <p className="text-xs text-center text-slate-400">
          Sync all pending items before completing the survey.
        </p>
      )}

      {completeMutation.isError && (
        <div data-testid="complete-survey-error" className="app-surface p-4 text-sm text-red-600">
          {isAuthFailure(completeMutation.error)
            ? authFailureMessage(completeMutation.error)
            : completeMutation.error instanceof Error
              ? completeMutation.error.message
              : 'Failed to complete survey. Please try again.'}
        </div>
      )}

      {/* Exit conference summary link */}
      {counts.findings > 0 && (
        <Link
          to="exit-summary"
          className="app-surface p-4 flex items-center gap-3 hover:border-[var(--brand-primary)] transition-colors"
        >
          <AlertTriangle size={20} className="text-[var(--brand-primary)] flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-bold text-[var(--brand-primary)]">Generate Exit Conference Summary</p>
            <p className="text-[10px] text-slate-400">Findings grouped by CfC, severity-ranked, with pattern analysis. Printable.</p>
          </div>
        </Link>
      )}

      {isCompleted && (
        <div className="app-surface p-4 text-center space-y-2">
          <CheckCircle size={32} className="mx-auto text-emerald-500" />
          <p className="text-sm font-semibold text-emerald-700">Survey completed</p>
          <p className="text-xs text-slate-500">Data has been synced and report review has been triggered.</p>
        </div>
      )}

      {/* Draft Report */}
      {isCompleted && (
        <div className="space-y-2">
          <Link
            to="../walkthrough?resume=completed"
            relative="path"
            data-testid="enter-guided-survey-btn"
            className="btn-secondary flex w-full items-center justify-center py-3 text-sm"
          >
            <ClipboardList size={16} className="mr-2" /> Enter Guided Survey
          </Link>
          <button
            type="button"
            data-testid="ai-draft-report-btn"
            onClick={handleGenerateDraft}
            disabled={isDraftGenerating}
            className="btn-primary w-full py-3 text-sm disabled:opacity-60"
          >
            {isDraftGenerating ? (
              <><Loader2 size={16} className="inline mr-2 animate-spin" /> Generating report...</>
            ) : (
              <><FileText size={16} className="inline mr-2" /> Generate Report</>
            )}
          </button>
          {draftError && (
            <p className="text-xs text-center text-red-500" data-testid="ai-draft-error">
              {draftError}
            </p>
          )}
        </div>
      )}
    </div>
    </FieldAppShell>
  )
}
